import { useState, useEffect, useRef } from "react";
import { GlassCard } from "../components/shared";
import { 
    MessageSquare, Send, Paperclip, Mic, Square, Trash2, 
    Play, Pause, Users, User, Plus, Search, FolderKanban,
    FileText, Image as ImageIcon, Volume2, ShieldAlert,
    Smile, Pin, Star, Info
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

export default function Chat() {
    const { user } = useAuth();
    const canManageGroups = ["super_admin", "hr", "lead"].includes(user?.role);
    
    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [chatUsers, setChatUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    
    // Search
    const [searchTerm, setSearchTerm] = useState("");
    
    // Send message state
    const [text, setText] = useState("");
    const [attachments, setAttachments] = useState([]);
    
    // Modals
    const [dmOpen, setDmOpen] = useState(false);
    const [groupOpen, setGroupOpen] = useState(false);
    
    // WhatsApp/Teams States
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [pinnedMessages, setPinnedMessages] = useState({});
    const [starredMessages, setStarredMessages] = useState(new Set());
    const prevMessagesCountRef = useRef(0);
    const [showDetails, setShowDetails] = useState(false);
    
    // Group Form State
    const [groupForm, setGroupForm] = useState({
        name: "",
        projectId: "",
        participants: [] // list of emails
    });
    
    // Audio Recording State
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // Fetch lists
    const fetchThreads = async () => {
        try {
            const { data } = await api.get("/chat/threads");
            setThreads(data.threads || []);
        } catch (err) {
            console.error("Failed to fetch chat threads:", err);
        } finally {
            setLoadingThreads(false);
        }
    };

    const fetchChatUsers = async () => {
        try {
            const { data } = await api.get("/chat/users");
            setChatUsers(data.users || []);
        } catch (err) {}
    };

    const fetchProjects = async () => {
        try {
            const { data } = await api.get("/projects");
            setProjects(data.items || []);
        } catch (err) {}
    };

    useEffect(() => {
        fetchThreads();
        fetchChatUsers();
        fetchProjects();
        
        // Poll threads and active chat messages
        const interval = setInterval(() => {
            fetchThreads();
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Poll messages of selected thread
    useEffect(() => {
        if (!selectedThread) return;
        prevMessagesCountRef.current = 0; // Reset count on thread swap to avoid beep on history load
        setShowDetails(false); // Close details pane on thread swap
        
        const fetchMessages = async () => {
            try {
                const { data } = await api.get(`/chat/threads/${selectedThread.id}/messages`);
                const newMsgs = data.messages || [];
                if (newMsgs.length > prevMessagesCountRef.current && prevMessagesCountRef.current > 0) {
                    const latest = newMsgs[newMsgs.length - 1];
                    if (latest && latest.sender_id !== user?.id) {
                        playNotificationSound();
                    }
                }
                prevMessagesCountRef.current = newMsgs.length;
                setMessages(newMsgs);
            } catch (err) {}
        };
        
        fetchMessages();
        const interval = setInterval(fetchMessages, 3000);
        return () => clearInterval(interval);
    }, [selectedThread, user?.id]);

    // Scroll to bottom on new message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Start DM
    const startDM = async (targetUser) => {
        try {
            const { data } = await api.post("/chat/threads", {
                type: "dm",
                participants: [targetUser.email]
            });
            setDmOpen(false);
            setSelectedThread(data);
            fetchThreads();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to start DM");
        }
    };

    const playNotificationSound = () => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5 note
            osc.frequency.setValueAtTime(880, ctx.currentTime + 0.08); // A5 note
            gain.gain.setValueAtTime(0.08, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.3);
        } catch (e) {
            console.error("Audio Context failed:", e);
        }
    };

    const pinMessage = (msg) => {
        setPinnedMessages(prev => ({
            ...prev,
            [selectedThread.id]: msg
        }));
        toast.success("Message pinned to top!");
    };

    const unpinMessage = () => {
        setPinnedMessages(prev => {
            const copy = { ...prev };
            delete copy[selectedThread.id];
            return copy;
        });
    };

    const toggleStarMessage = (id) => {
        setStarredMessages(prev => {
            const copy = new Set(prev);
            if (copy.has(id)) {
                copy.delete(id);
            } else {
                copy.add(id);
            }
            return copy;
        });
    };

    const handleDeleteMessage = async (msgId) => {
        if (!confirm("Are you sure you want to delete this message?")) return;
        try {
            await api.delete(`/chat/messages/${msgId}`);
            toast.success("Message deleted");
            setMessages(prev => prev.filter(m => m.id !== msgId));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete message");
        }
    };

    const handleDeleteThread = async (chatId) => {
        if (!confirm("Are you sure you want to delete this chat? This will permanently delete the conversation and all messages for everyone.")) return;
        try {
            await api.delete(`/chat/threads/${chatId}`);
            toast.success("Chat deleted successfully");
            setSelectedThread(null);
            fetchThreads();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete chat thread");
        }
    };

    const renderMessageStatusTicks = (m) => {
        if (m.sender_id !== user?.id) return null;
        
        if (selectedThread?.type === "dm") {
            const recipientEmail = selectedThread.participants.find(p => p !== user?.email);
            const recipientUser = chatUsers.find(u => u.email === recipientEmail);
            const isRead = m.read_by?.includes(recipientUser?.id);
            const isOnline = recipientUser?.online;
            
            if (isRead) {
                return (
                    <span className="inline-flex ml-1.5 align-middle text-teal-400">
                        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                            <path d="M11.13 19.34a1 1 0 0 1-.76-.34l-5-5.75a1 1 0 1 1 1.52-1.3l4.16 4.79L19.78 6.46a1 1 0 0 1 1.48 1.35l-9.35 11.2a1 1 0 0 1-.78.33z"/>
                            <path d="M7.13 19.34a1 1 0 0 1-.76-.34l-5-5.75a1 1 0 1 1 1.52-1.3l4.16 4.79 3.44-4.13a1 1 0 1 1 1.54 1.28l-4.12 4.95a1 1 0 0 1-.78.33z"/>
                        </svg>
                    </span>
                );
            } else if (isOnline) {
                return (
                    <span className="inline-flex ml-1.5 align-middle text-slate-400 dark:text-slate-500">
                        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                            <path d="M11.13 19.34a1 1 0 0 1-.76-.34l-5-5.75a1 1 0 1 1 1.52-1.3l4.16 4.79L19.78 6.46a1 1 0 0 1 1.48 1.35l-9.35 11.2a1 1 0 0 1-.78.33z"/>
                            <path d="M7.13 19.34a1 1 0 0 1-.76-.34l-5-5.75a1 1 0 1 1 1.52-1.3l4.16 4.79 3.44-4.13a1 1 0 1 1 1.54 1.28l-4.12 4.95a1 1 0 0 1-.78.33z"/>
                        </svg>
                    </span>
                );
            }
        }
        
        return (
            <span className="inline-flex ml-1.5 align-middle text-slate-400 dark:text-slate-500">
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M10 19.34a1 1 0 0 1-.76-.34l-5-5.75a1 1 0 1 1 1.52-1.3l4.16 4.79 8.28-9.93a1 1 0 1 1 1.54 1.28l-9.04 10.85a1 1 0 0 1-.78.33z"/>
                </svg>
            </span>
        );
    };

    // Create Group Chat
    const createGroupChat = async (e) => {
        e.preventDefault();
        if (!groupForm.name) {
            toast.error("Group name is required");
            return;
        }
        
        // Auto-fetch project members if project selected
        let finalParticipants = [...groupForm.participants];
        if (groupForm.projectId) {
            const proj = projects.find(p => p.id === groupForm.projectId);
            if (proj) {
                if (proj.members) finalParticipants.push(...proj.members);
                if (proj.leader && proj.leader.includes("@")) {
                    finalParticipants.push(proj.leader);
                }
            }
        }
        
        try {
            const { data } = await api.post("/chat/threads", {
                type: "group",
                name: groupForm.name,
                project_id: groupForm.projectId || null,
                participants: Array.from(new Set(finalParticipants))
            });
            setGroupOpen(false);
            setSelectedThread(data);
            setGroupForm({ name: "", projectId: "", participants: [] });
            fetchThreads();
            toast.success("Group chat created!");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create group");
        }
    };

    // Handle File Selection
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                let fileType = "file";
                if (file.type.startsWith("image/")) fileType = "image";
                else if (file.type.startsWith("audio/")) fileType = "audio";
                else if (file.type.startsWith("video/")) fileType = "video";
                else if (file.type.includes("pdf")) fileType = "pdf";
                
                setAttachments(prev => [...prev, {
                    name: file.name,
                    type: fileType,
                    content: reader.result
                }]);
            };
            reader.readAsDataURL(file);
        });
    };

    // Audio recording handlers
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioChunksRef.current = [];
            
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                setAudioBlob(audioBlob);
                
                // Read blob as base64 data URI to send in message
                const reader = new FileReader();
                reader.onloadend = () => {
                    setAttachments(prev => [...prev, {
                        name: "voice_note.webm",
                        type: "audio",
                        content: reader.result
                    }]);
                };
                reader.readAsDataURL(audioBlob);
                
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };
            
            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (err) {
            toast.error("Microphone access denied or unavailable");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const cancelRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
            setAttachments(prev => prev.filter(a => a.name !== "voice_note.webm"));
            setAudioBlob(null);
        }
    };

    // Send message
    const handleSend = async (e) => {
        e.preventDefault();
        if (!text.trim() && attachments.length === 0) return;
        
        try {
            await api.post(`/chat/threads/${selectedThread.id}/messages`, {
                message: text,
                attachments: attachments
            });
            setText("");
            setAttachments([]);
            setAudioBlob(null);
            
            // Reload messages
            const { data } = await api.get(`/chat/threads/${selectedThread.id}/messages`);
            setMessages(data.messages || []);
        } catch (err) {
            toast.error("Failed to send message");
        }
    };

    const formatTime = (sec) => {
        const mins = Math.floor(sec / 60);
        const secs = sec % 60;
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    // Filter threads by search term
    const filteredThreads = threads.filter(t => {
        if (t.type === "dm") {
            return t.dm_user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return t.name?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="flex h-[calc(100vh-140px)] gap-6 overflow-hidden">
            {/* Left Side: Threads List */}
            <GlassCard className="w-80 flex flex-col p-4 border border-teal-100/50 dark:border-teal-900/50 h-full overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Chats</h2>
                    <div className="flex gap-2">
                        {/* New DM Button */}
                        <Dialog open={dmOpen} onOpenChange={setDmOpen}>
                            <DialogTrigger asChild>
                                <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg" title="New DM">
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[400px]">
                                <DialogHeader>
                                    <DialogTitle>Start a Direct Message</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-2 max-h-[350px] overflow-y-auto">
                                    {chatUsers.length === 0 ? (
                                        <p className="text-xs text-muted-foreground text-center">No matching managers or admins found to DM.</p>
                                    ) : (
                                        chatUsers.map(u => (
                                            <div 
                                                key={u.id} 
                                                onClick={() => startDM(u)}
                                                className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer border transition"
                                            >
                                                <div className="relative">
                                                    {u.avatar ? (
                                                        <img src={u.avatar} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold uppercase">
                                                            {u.name[0]}
                                                        </div>
                                                    )}
                                                    <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white
                                                        ${u.online ? "bg-emerald-500" : "bg-slate-300"}`} 
                                                    />
                                                </div>
                                                <div className="text-left">
                                                    <div className="text-xs font-semibold">{u.name}</div>
                                                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{u.role}</div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* New Group Button (Lead/Admin/HR only) */}
                        {canManageGroups && (
                            <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
                                <DialogTrigger asChild>
                                    <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg" title="New Group discussion">
                                        <Users className="w-4 h-4" />
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[450px]">
                                    <DialogHeader>
                                        <DialogTitle>Create Group Chat</DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={createGroupChat} className="space-y-4">
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Group Name</Label>
                                            <Input required placeholder="e.g. Frontend Dev Team" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} className="mt-1" />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Link to Project (Optional)</Label>
                                            <select 
                                                value={groupForm.projectId} 
                                                onChange={(e) => setGroupForm({ ...groupForm, projectId: e.target.value })}
                                                className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm"
                                            >
                                                <option value="">-- Select Project --</option>
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                            <p className="text-[10px] text-muted-foreground mt-1">If selected, project members are automatically assigned to this group chat.</p>
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Additional Participant Emails (Comma-separated)</Label>
                                            <Input placeholder="e.g. employee1@domain.com, lead2@domain.com" value={groupForm.participants.join(", ")} onChange={(e) => setGroupForm({ ...groupForm, participants: e.target.value.split(",").map(x => x.trim()) })} className="mt-1" />
                                        </div>
                                        <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold">Create Group</Button>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>
                </div>

                {/* Search Bar */}
                <div className="relative mb-3">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input 
                        placeholder="Search chats..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        className="pl-9 h-9 text-xs rounded-xl"
                    />
                </div>

                {/* Threads List Container */}
                <div className="flex-1 overflow-y-auto space-y-1 pr-1">
                    {loadingThreads ? (
                        <div className="text-center text-xs text-muted-foreground py-8">Loading chats...</div>
                    ) : filteredThreads.length === 0 ? (
                        <div className="text-center text-xs text-muted-foreground py-8">No chats found.</div>
                    ) : (
                        filteredThreads.map(t => {
                            const active = selectedThread?.id === t.id;
                            const isGroup = t.type === "group";
                            const displayName = isGroup ? t.name : (t.dm_user?.name || "Chat Room");
                            const displayAvatar = isGroup ? null : t.dm_user?.avatar;
                            const online = isGroup ? false : t.dm_user?.online;
                            const unread = t.unread_count > 0;
                            
                            return (
                                <div
                                    key={t.id}
                                    onClick={() => setSelectedThread(t)}
                                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-150 border
                                        ${active 
                                            ? "bg-teal-600 text-white shadow-md shadow-teal-600/10 border-transparent" 
                                            : unread
                                                ? "bg-teal-500/5 dark:bg-teal-500/10 hover:bg-teal-500/10 dark:hover:bg-teal-500/20 border-teal-500/20 shadow-sm"
                                                : "hover:bg-slate-50 dark:hover:bg-slate-900 border-transparent hover:border-black/5 dark:hover:border-white/5"
                                        }`}
                                >
                                    {/* Avatar */}
                                    <div className="relative">
                                        {isGroup ? (
                                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white
                                                ${active ? "bg-teal-700" : "bg-teal-600"}`}>
                                                <FolderKanban className="w-5 h-5" />
                                            </div>
                                        ) : displayAvatar ? (
                                            <img src={displayAvatar} alt={displayName} className="w-9 h-9 rounded-full object-cover border" />
                                        ) : (
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold uppercase border
                                                ${active ? "bg-teal-700 text-white" : "bg-slate-200 text-slate-800"}`}>
                                                {displayName[0]}
                                            </div>
                                        )}
                                        {!isGroup && (
                                            <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2
                                                ${active ? "border-teal-600" : "border-white"}
                                                ${online ? "bg-emerald-500" : "bg-slate-300"}`} 
                                            />
                                        )}
                                    </div>

                                    {/* Info / Preview */}
                                    <div className="flex-1 min-w-0 text-left">
                                        <div className="flex justify-between items-baseline">
                                            <div className={`text-xs font-semibold truncate ${
                                                active 
                                                    ? "text-white" 
                                                    : unread 
                                                        ? "text-teal-600 dark:text-teal-400 font-bold" 
                                                        : "text-slate-800 dark:text-slate-200"
                                            }`}>
                                                {displayName}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className={`text-[9px] ${active ? "text-teal-200" : unread ? "text-teal-600 dark:text-teal-400 font-bold" : "text-muted-foreground"}`}>
                                                    {t.latest_message ? new Date(t.latest_message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
                                                </span>
                                                {unread && !active && (
                                                    <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse shrink-0" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center gap-2">
                                            <div className={`text-[10px] truncate flex-1 ${
                                                active 
                                                    ? "text-teal-100" 
                                                    : unread 
                                                        ? "text-slate-800 dark:text-slate-100 font-semibold" 
                                                        : "text-slate-500"
                                            }`}>
                                                {t.latest_message ? (
                                                    t.latest_message.message || "Sent an attachment"
                                                ) : (
                                                    <span className="italic">No messages yet</span>
                                                )}
                                            </div>
                                            {unread && !active && (
                                                <span className="text-[9px] bg-teal-600 text-white rounded-full px-1.5 py-0.25 font-bold scale-90">
                                                    {t.unread_count}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </GlassCard>

            {/* Right Side: Conversation Area */}
            <GlassCard className="flex-1 flex flex-col border border-teal-100/50 dark:border-teal-900/50 h-full overflow-hidden p-0 relative">
                {selectedThread ? (
                    <>
                        {/* Conversation Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-black/5 dark:border-white/10 bg-slate-50/50 dark:bg-slate-900/50">
                            <div 
                                className={`flex items-center gap-3 ${selectedThread.type === "dm" ? "cursor-pointer hover:opacity-85 transition" : ""}`}
                                onClick={() => selectedThread.type === "dm" && setShowDetails(!showDetails)}
                            >
                                <div className="relative">
                                    {selectedThread.type === "group" ? (
                                        <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400 flex items-center justify-center">
                                            <FolderKanban className="w-5 h-5" />
                                        </div>
                                    ) : selectedThread.dm_user?.avatar ? (
                                        <img src={selectedThread.dm_user.avatar} alt={selectedThread.dm_user.name} className="w-10 h-10 rounded-full object-cover border" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-sm border uppercase">
                                            {(selectedThread.dm_user?.name || "Chat Room")[0]}
                                        </div>
                                    )}
                                    {selectedThread.type === "dm" && (
                                        <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white
                                            ${selectedThread.dm_user?.online ? "bg-emerald-500" : "bg-slate-300"}`} 
                                        />
                                    )}
                                </div>
                                <div className="text-left">
                                    <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                        {selectedThread.type === "group" ? selectedThread.name : (selectedThread.dm_user?.name || "Direct Message")}
                                    </h3>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide truncate max-w-[280px] text-left">
                                        {selectedThread.type === "group" 
                                            ? `Group Members: ${selectedThread.participants.join(", ")}` 
                                            : (chatUsers.find(u => u.email === selectedThread.dm_user?.email)?.online ? "🟢 Online" : "⚪ Offline")
                                        }
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-1.5 shrink-0">
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-9 w-9 rounded-xl text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-955/20 transition"
                                    onClick={() => handleDeleteThread(selectedThread.id)}
                                    title="Delete Chat Thread"
                                >
                                    <Trash2 className="w-4.5 h-4.5" />
                                </Button>
                                {selectedThread.type === "dm" && (
                                    <Button 
                                        type="button" 
                                        size="icon" 
                                        variant="ghost" 
                                        className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                        onClick={() => setShowDetails(!showDetails)}
                                    >
                                        <Info className="w-5 h-5" />
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 flex overflow-hidden">
                            {/* Message Feed & Input Column */}
                            <div className="flex-1 flex flex-col min-w-0">
                                {/* Pinned Message Banner */}
                                {pinnedMessages[selectedThread.id] && (
                                    <div className="bg-teal-50/70 dark:bg-teal-950/20 px-6 py-2 flex items-center justify-between border-b border-teal-100/50 dark:border-teal-900/30 text-[10px] text-teal-800 dark:text-teal-400 shrink-0">
                                        <div className="flex items-center gap-1.5 truncate">
                                            <Pin className="w-3 h-3 text-teal-600 dark:text-teal-400 shrink-0" />
                                            <span className="font-bold text-teal-950 dark:text-teal-300">Pinned:</span>
                                            <span className="truncate italic">"{pinnedMessages[selectedThread.id].message || "Attachment"}"</span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-4">
                                            <button 
                                                onClick={() => {
                                                    const el = document.getElementById(`msg-${pinnedMessages[selectedThread.id].id}`);
                                                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                                                }}
                                                className="hover:underline font-semibold text-teal-700 dark:text-teal-400"
                                            >
                                                Go to Message
                                            </button>
                                            <span className="text-teal-355">•</span>
                                            <button onClick={unpinMessage} className="hover:underline text-rose-500 font-semibold">
                                                Unpin
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Messages Feed Container */}
                                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-slate-50/10">
                                    {messages.map((m) => {
                                        const isSelf = m.sender_id === user["id"];
                                        return (
                                            <div key={m.id} id={`msg-${m.id}`} className={`flex items-start gap-3 max-w-[75%] ${isSelf ? "ml-auto flex-row-reverse" : "mr-auto"}`}>
                                                {/* Avatar */}
                                                {!isSelf && (
                                                    m.sender_avatar ? (
                                                        <img src={m.sender_avatar} alt={m.sender_name} className="w-8 h-8 rounded-full object-cover border mt-1" />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold mt-1 uppercase">
                                                            {m.sender_name[0]}
                                                        </div>
                                                    )
                                                )}

                                                {/* Content Bubble */}
                                                <div className="flex flex-col group relative">
                                                    {!isSelf && (
                                                        <span className="text-[10px] font-semibold text-muted-foreground mb-0.5 ml-1 text-left">{m.sender_name}</span>
                                                    )}
                                                    
                                                    <div className="flex items-center gap-2">
                                                        <div className={`p-3 rounded-2xl text-xs leading-relaxed text-left break-words
                                                            ${isSelf 
                                                                ? "bg-teal-600 text-white rounded-tr-none shadow-sm" 
                                                                : "bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-tl-none text-slate-800 dark:text-slate-200"
                                                            }`}
                                                        >
                                                            {/* Text Content */}
                                                            {m.message && <p>{m.message}</p>}

                                                            {/* Attachments */}
                                                            {m.attachments && m.attachments.length > 0 && (
                                                                <div className="mt-2 space-y-2">
                                                                    {m.attachments.map((att, index) => {
                                                                        if (att.type === "image") {
                                                                            return (
                                                                                <div key={index} className="rounded-lg overflow-hidden border bg-black max-w-[240px]">
                                                                                    <img src={att.url} alt={att.name} className="w-full h-auto object-cover max-h-[160px] cursor-pointer hover:opacity-90 transition" onClick={() => window.open(att.url, '_blank')} />
                                                                                </div>
                                                                            );
                                                                        } else if (att.type === "audio") {
                                                                            return (
                                                                                <div key={index} className="flex items-center gap-2 p-1 bg-black/5 dark:bg-white/5 rounded-lg border min-w-[200px]">
                                                                                    <Volume2 className="w-4 h-4 text-teal-600 dark:text-teal-400 shrink-0" />
                                                                                    <audio controls src={att.url} className="w-full h-7 text-xs" />
                                                                                </div>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <a 
                                                                                    key={index} 
                                                                                    href={att.url} 
                                                                                    target="_blank" 
                                                                                    rel="noopener noreferrer"
                                                                                    className="flex items-center gap-2.5 p-2 bg-slate-50 dark:bg-slate-950/40 hover:bg-slate-100/80 rounded-xl border border-black/5 dark:border-white/10 transition text-inherit text-[11px]"
                                                                                >
                                                                                    <FileText className="w-5 h-5 text-teal-500" />
                                                                                    <div className="flex-1 truncate text-left">
                                                                                        <div className="font-semibold truncate">{att.name}</div>
                                                                                        <div className="text-[9px] text-muted-foreground uppercase">{att.type} file</div>
                                                                                    </div>
                                                                                </a>
                                                                            );
                                                                        }
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* WhatsApp Quick Actions (Star, Pin, Delete) on Hover */}
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 shrink-0">
                                                            <button 
                                                                type="button"
                                                                onClick={() => toggleStarMessage(m.id)}
                                                                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 hover:text-amber-500 transition"
                                                                title="Star Message"
                                                            >
                                                                <Star className={`w-3.5 h-3.5 ${starredMessages.has(m.id) ? "fill-amber-400 text-amber-500" : ""}`} />
                                                            </button>
                                                            <button 
                                                                type="button"
                                                                onClick={() => pinMessage(m)}
                                                                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 hover:text-teal-600 transition"
                                                                title="Pin Message"
                                                            >
                                                                <Pin className="w-3.5 h-3.5" />
                                                            </button>
                                                            {(isSelf || ["super_admin", "hr"].includes(user?.role)) && (
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => handleDeleteMessage(m.id)}
                                                                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 hover:text-rose-600 transition"
                                                                    title="Delete Message"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Time tag */}
                                                    <span className={`text-[8px] text-muted-foreground mt-1 flex items-center gap-1 ${isSelf ? "text-right justify-end" : "text-left justify-start"}`}>
                                                        {starredMessages.has(m.id) && <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-500 shrink-0" />}
                                                        {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        {isSelf && renderMessageStatusTicks(m)}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Recording status panel */}
                                {isRecording && (
                                    <div className="px-6 py-2 bg-red-50 text-red-700 dark:bg-red-950/20 dark:text-red-400 flex items-center justify-between border-t border-red-200/50">
                                        <div className="flex items-center gap-2 text-xs font-semibold animate-pulse">
                                            <Mic className="w-4 h-4" />
                                            <span>Recording Audio... {formatTime(recordingTime)}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="destructive" onClick={cancelRecording} className="h-8 rounded-lg text-xs">
                                                Cancel
                                            </Button>
                                            <Button size="sm" onClick={stopRecording} className="h-8 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs flex items-center gap-1">
                                                <Square className="w-3.5 h-3.5 fill-white" /> Stop & Add Voice Note
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Upload Queue Previews */}
                                {attachments.length > 0 && (
                                    <div className="px-6 py-2 border-t border-black/5 dark:border-white/10 flex flex-wrap gap-2 bg-slate-50 dark:bg-slate-900/20">
                                        {attachments.map((att, idx) => (
                                            <div key={idx} className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg bg-teal-50 dark:bg-teal-950/20 border text-xs text-teal-700 dark:text-teal-400 font-medium">
                                                {att.type === "image" ? <ImageIcon className="w-3.5 h-3.5" /> : att.type === "audio" ? <Volume2 className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                                                <span className="truncate max-w-[120px]">{att.name}</span>
                                                <button 
                                                    type="button" 
                                                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))} 
                                                    className="p-0.5 hover:text-red-500 rounded"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Input Area */}
                                <form onSubmit={handleSend} className="p-4 border-t border-black/5 dark:border-white/10 flex gap-2 items-center bg-white dark:bg-[hsl(var(--surface))]/40">
                                    {/* Attachment trigger */}
                                    <Button 
                                        type="button" 
                                        size="icon" 
                                        variant="outline" 
                                        className="h-11 w-11 rounded-xl text-muted-foreground hover:text-foreground shrink-0"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isRecording}
                                    >
                                        <Paperclip className="w-5 h-5" />
                                    </Button>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef} 
                                        className="hidden" 
                                        multiple 
                                        onChange={handleFileChange} 
                                        accept="image/*,audio/*,video/*,application/pdf,.doc,.docx,.ppt,.pptx"
                                    />

                                    {/* Audio recording trigger */}
                                    <Button 
                                        type="button" 
                                        size="icon" 
                                        variant="outline" 
                                        className={`h-11 w-11 rounded-xl shrink-0 transition
                                            ${isRecording 
                                                ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100" 
                                                : "text-muted-foreground hover:text-foreground"
                                            }`}
                                        onClick={isRecording ? stopRecording : startRecording}
                                    >
                                        <Mic className="w-5 h-5" />
                                    </Button>

                                     {/* Emoji selector trigger */}
                                     <div className="relative">
                                         <Button 
                                             type="button" 
                                             size="icon" 
                                             variant="outline" 
                                             className="h-11 w-11 rounded-xl text-muted-foreground hover:text-foreground shrink-0"
                                             onClick={() => setEmojiOpen(!emojiOpen)}
                                             disabled={isRecording}
                                         >
                                             <Smile className="w-5 h-5" />
                                         </Button>
                                         
                                         {emojiOpen && (
                                             <div className="absolute bottom-14 left-0 z-50 p-2 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-xl w-60 grid grid-cols-6 gap-1">
                                                 {[
                                                     "😀", "😂", "😍", "👍", "🔥", "🎉", 
                                                     "🚀", "👏", "🙌", "❤️", "⭐", "✅",
                                                     "🙏", "💡", "🎯", "🌟", "📌", "❌"
                                                 ].map(emoji => (
                                                     <button 
                                                         key={emoji} 
                                                         type="button"
                                                         onClick={() => {
                                                             setText(prev => prev + emoji);
                                                             setEmojiOpen(false);
                                                         }}
                                                         className="text-lg hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded transition"
                                                     >
                                                         {emoji}
                                                     </button>
                                                 ))}
                                             </div>
                                         )}
                                     </div>

                                    {/* Message box */}
                                    <Input 
                                        placeholder={isRecording ? "Finish recording before typing..." : "Type a message..."} 
                                        value={text} 
                                        onChange={(e) => setText(e.target.value)}
                                        className="flex-1 h-11 text-xs rounded-xl"
                                        disabled={isRecording}
                                    />

                                    {/* Send trigger */}
                                    <Button 
                                        type="submit" 
                                        size="icon" 
                                        className="h-11 w-11 rounded-xl bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                                        disabled={isRecording}
                                    >
                                        <Send className="w-5 h-5" />
                                    </Button>
                                </form>
                            </div>

                            {/* Sliding User Details Sidebar */}
                            {showDetails && selectedThread.type === "dm" && (
                                <div className="w-72 border-l border-slate-150 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col shrink-0 overflow-y-auto animate-in slide-in-from-right duration-200">
                                    <div className="p-6 flex flex-col items-center text-center border-b relative">
                                        <button 
                                            onClick={() => setShowDetails(false)}
                                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm font-bold p-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                                        >
                                            ✕
                                        </button>
                                        <div className="w-20 h-20 rounded-full border-2 border-teal-500 overflow-hidden shadow-md mb-3 bg-slate-100 shrink-0">
                                            {selectedThread.dm_user?.avatar ? (
                                                <img src={selectedThread.dm_user.avatar} alt={selectedThread.dm_user.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-teal-600 to-emerald-400 grid place-items-center text-white text-2xl font-bold uppercase">
                                                    {(selectedThread.dm_user?.name || "U")[0]}
                                                </div>
                                            )}
                                        </div>
                                        <h4 className="font-heading font-bold text-sm text-slate-800 dark:text-slate-100">{selectedThread.dm_user?.name}</h4>
                                        <p className="text-[10px] text-muted-foreground mt-0.5 break-all max-w-full">{selectedThread.dm_user?.email}</p>
                                        <span className={`mt-2.5 text-[9px] font-bold uppercase px-2.5 py-0.5 rounded-full border
                                            ${chatUsers.find(u => u.email === selectedThread.dm_user?.email)?.online
                                                ? "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400"
                                                : "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-850 dark:text-slate-400"
                                            }`}>
                                            {chatUsers.find(u => u.email === selectedThread.dm_user?.email)?.online ? "Online" : "Offline"}
                                        </span>
                                    </div>
                                    
                                    <div className="p-5 space-y-4 text-left">
                                        <div>
                                            <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Bio / Status</span>
                                            <p className="text-xs text-slate-700 dark:text-slate-300 mt-1 font-medium leading-relaxed italic">
                                                {selectedThread.dm_user?.bio || selectedThread.dm_user?.notes || "Hey there! I am using ParshHR."}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Designation</span>
                                            <p className="text-xs text-slate-800 dark:text-slate-200 mt-1 font-semibold">
                                                {selectedThread.dm_user?.designation || "Team Member"}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Department</span>
                                            <p className="text-xs text-slate-800 dark:text-slate-200 mt-1 font-semibold">
                                                {selectedThread.dm_user?.department || "Operations"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    /* Splash view */
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                        <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400 flex items-center justify-center mb-4">
                            <MessageSquare className="w-8 h-8" />
                        </div>
                        <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Welcome to ParshHR Chat</h3>
                        <p className="text-xs text-muted-foreground max-w-sm mt-1 leading-relaxed">
                            DM Managers or leads directly, or join discussion groups created by team leaders for project milestones.
                        </p>
                    </div>
                )}
            </GlassCard>
        </div>
    );
}
